// src/components/ui/sections/TeamMemberOverview/TeamMemberOverview.jsx
import React from 'react';
import { 
  Clock, 
  Calendar, 
  Activity, 
  Mail, 
  User,
  Crown,
  Shield,
  Circle
} from 'lucide-react';
import { cn } from '../../../../utils/cn';

const TeamMemberOverview = ({
  member
}) => {
  // Online status based on last_seen_at
  const getOnlineStatus = (lastSeen) => {
    if (!lastSeen) return { 
      status: 'offline', 
      color: 'text-slate-500', 
      bgColor: 'bg-slate-500',
      label: 'Offline'
    };
    
    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffMinutes = (now - lastSeenDate) / (1000 * 60);
    
    if (diffMinutes < 5) {
      return { 
        status: 'online', 
        color: 'text-emerald-400', 
        bgColor: 'bg-emerald-400',
        label: 'Online'
      };
    } else if (diffMinutes < 30) {
      return { 
        status: 'away', 
        color: 'text-amber-400', 
        bgColor: 'bg-amber-400',
        label: 'Away'
      };
    } else {
      return { 
        status: 'offline', 
        color: 'text-slate-500', 
        bgColor: 'bg-slate-500',
        label: 'Offline'
      };
    }
  };

  // Format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Calculate time since joined
  const getTimeSinceJoined = (createdAt) => {
    if (!createdAt) return 'Unknown';
    
    const joinDate = new Date(createdAt);
    const now = new Date();
    const diffMonths = Math.floor((now - joinDate) / (1000 * 60 * 60 * 24 * 30));
    
    if (diffMonths < 1) {
      const diffDays = Math.floor((now - joinDate) / (1000 * 60 * 60 * 24));
      return diffDays === 0 ? 'Today' : `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else if (diffMonths < 12) {
      return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
    } else {
      const diffYears = Math.floor(diffMonths / 12);
      return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
    }
  };

  // Role configuration
  const getRoleConfig = (role) => {
    switch (role) {
      case 'admin':
        return {
          icon: Crown,
          label: 'Administrator',
          color: 'text-amber-400',
          bgColor: 'bg-amber-500/20',
          borderColor: 'border-amber-500/30'
        };
      case 'manager':
        return {
          icon: Shield,
          label: 'Manager',
          color: 'text-purple-400',
          bgColor: 'bg-purple-500/20',
          borderColor: 'border-purple-500/30'
        };
      case 'staff':
        return {
          icon: User,
          label: 'Staff',
          color: 'text-blue-400',
          bgColor: 'bg-blue-500/20',
          borderColor: 'border-blue-500/30'
        };
      default:
        return {
          icon: User,
          label: 'Unknown',
          color: 'text-slate-400',
          bgColor: 'bg-slate-500/20',
          borderColor: 'border-slate-500/30'
        };
    }
  };

  const onlineStatus = getOnlineStatus(member?.last_seen_at);
  const roleConfig = getRoleConfig(member?.role);
  const RoleIcon = roleConfig.icon;

  return (
    <div className="p-6 space-y-6">
      {/* Status Card */}
      <div className="bg-slate-700/30 border border-slate-600/30 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-slate-200 mb-4">Current Status</h3>
        
        <div className="grid grid-cols-2 gap-4">
          {/* Online Status */}
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-3 h-3 rounded-full",
              onlineStatus.bgColor
            )}></div>
            <div>
              <p className="text-slate-400 text-sm">Status</p>
              <p className={cn("font-medium", onlineStatus.color)}>
                {onlineStatus.label}
              </p>
            </div>
          </div>

          {/* Role */}
          <div className="flex items-center gap-3">
            <RoleIcon className={cn("w-4 h-4", roleConfig.color)} />
            <div>
              <p className="text-slate-400 text-sm">Role</p>
              <p className="font-medium text-slate-200">{roleConfig.label}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Member Information */}
      <div className="bg-slate-700/30 border border-slate-600/30 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-slate-200 mb-4">Member Information</h3>
        
        <div className="space-y-4">
          {/* Email */}
          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-slate-400" />
            <div>
              <p className="text-slate-400 text-sm">Email Address</p>
              <p className="font-medium text-slate-200">{member?.email}</p>
            </div>
          </div>

          {/* Member Since */}
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-slate-400" />
            <div>
              <p className="text-slate-400 text-sm">Member Since</p>
              <p className="font-medium text-slate-200">
                {getTimeSinceJoined(member?.created_at)}
              </p>
            </div>
          </div>

          {/* Last Seen */}
          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 text-slate-400" />
            <div>
              <p className="text-slate-400 text-sm">Last Seen</p>
              <p className="font-medium text-slate-200">
                {formatDate(member?.last_seen_at)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Summary */}
      <div className="bg-slate-700/30 border border-slate-600/30 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-slate-200 mb-4">Activity Summary</h3>
        
        <div className="space-y-4">
          {/* Note: These would be real metrics if activity logging was implemented */}
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-slate-600/30 rounded-lg">
              <div className="text-slate-400 text-sm mb-1">Events</div>
              <div className="text-xl font-semibold text-slate-200">—</div>
              <div className="text-xs text-slate-500">This month</div>
            </div>
            
            <div className="p-3 bg-slate-600/30 rounded-lg">
              <div className="text-slate-400 text-sm mb-1">Customers</div>
              <div className="text-xl font-semibold text-slate-200">—</div>
              <div className="text-xs text-slate-500">Managed</div>
            </div>
            
            <div className="p-3 bg-slate-600/30 rounded-lg">
              <div className="text-slate-400 text-sm mb-1">Invoices</div>
              <div className="text-xl font-semibold text-slate-200">—</div>
              <div className="text-xs text-slate-500">Created</div>
            </div>
          </div>

          {/* Activity Note */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-400" />
              <p className="text-blue-200 text-sm font-medium">Activity Tracking</p>
            </div>
            <p className="text-blue-300/80 text-xs mt-1">
              Detailed activity metrics will be available once activity logging is implemented.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-slate-700/30 border border-slate-600/30 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-slate-200 mb-4">Quick Actions</h3>
        
        <div className="space-y-2">
          <button className="w-full text-left p-3 rounded-lg bg-slate-600/30 hover:bg-slate-600/50 transition-colors text-slate-300 hover:text-slate-200">
            Send message to {member?.name?.split(' ')[0]}
          </button>
          
          <button className="w-full text-left p-3 rounded-lg bg-slate-600/30 hover:bg-slate-600/50 transition-colors text-slate-300 hover:text-slate-200">
            View {member?.name?.split(' ')[0]}'s calendar
          </button>
          
          <button className="w-full text-left p-3 rounded-lg bg-slate-600/30 hover:bg-slate-600/50 transition-colors text-slate-300 hover:text-slate-200">
            See {member?.name?.split(' ')[0]}'s recent activity
          </button>
        </div>

        <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/20 rounded text-xs text-amber-300">
          <strong>Note:</strong> Quick actions are placeholder features for future implementation.
        </div>
      </div>
    </div>
  );
};

export default TeamMemberOverview;